import { useCallback } from 'react';
import { useSeatEventGuests } from '../../../hooks/useEventGuests';
import type { SeatGuestsInput } from '../../../api/eventGuests';
import { useDialog } from '../../../context/DialogContext';
import { extractApiErrorCode, extractApiErrorMessage } from '../../../utils/apiError';

/**
 * Seat guests, asking before squeezing a table past its seats: the server
 * refuses with `OVER_CAPACITY`, the producer is asked "Seat anyway?", and a yes
 * sends it again with `allowOverCapacity`. Any other refusal is said plainly.
 * Answers whether the guests were seated.
 */
export function useSeatGuests(eventId: string) {
  const seat = useSeatEventGuests(eventId);
  const { confirm, alert } = useDialog();
  const { mutateAsync } = seat;

  const run = useCallback(
    async (input: SeatGuestsInput): Promise<boolean> => {
      try {
        await mutateAsync(input);
        return true;
      } catch (err) {
        const message = extractApiErrorMessage(err, 'The guests could not be seated.');
        if (extractApiErrorCode(err) === 'OVER_CAPACITY' && !input.allowOverCapacity) {
          const ok = await confirm({
            title: 'Table is full',
            message: `${message}. Seat them anyway?`,
            confirmLabel: 'Seat anyway',
            variant: 'warning',
          });
          return ok ? run({ ...input, allowOverCapacity: true }) : false;
        }
        await alert({ title: 'Could not seat', message, variant: 'error' });
        return false;
      }
    },
    [mutateAsync, confirm, alert],
  );

  return { seat: run, isPending: seat.isPending };
}
